import type { SerializableNode, SerializerProps } from '@/serializer';
import type { TSwitchElement } from '@/switchElement';
import { updateNodeText } from '@/utils/updateNodeText';
import type { JSONContent } from '@tiptap/core';
import { produce } from 'immer';
import type { ReactNode } from 'react';
import { getElement } from '../../serializer/getElement';
import { SwitchElement } from '../../switchElement/Switch';
import { SerializeTextLeaf, type TTextLeaf } from '../../textLeaf';
import type { Recommendation } from '../../types';
interface ExtendedSerializerProps extends SerializerProps {
  node: JSONContent & SerializableNode;
  recommendation: Recommendation;
  onChange: (recommendation: Recommendation) => void;
  isChecked: boolean;
  isLive: boolean;
  isSelectAllChecked: boolean;
}

const serializeNode = (
  {
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    createKey = (entry) => entry[1].join(','),
    children,
    recommendation,
    onChange,
    isChecked,
    isLive,
    isSelectAllChecked,
  }: ExtendedSerializerProps,
  path = [0], // internal path tracking
): ReactNode => {
  const key = createKey([node, path]);

  //TODO: Add serializer for recommendations
  const isImage = node?.type === 'image';
  const isParagraph = node?.type === 'paragraph';
  const isHeading = node?.type === 'heading';
  const mergeTag = node?.attrs?.mergeTag;
  const isRootContainerImage = node?.type === 'container' && rootStyle && node?.attrs?.backgroundImage;

  if (isRootContainerImage) {
    node = {
      ...node,
      attrs: {
        ...node.attrs,
        backgroundImage: recommendation?.logo_url,
      },
    } as JSONContent & SerializableNode;
  }

  // Swap out images
  if (isImage && mergeTag) {
    if (mergeTag === 'recommendation.image') {
      node = produce(node, (draft) => {
        if (draft.attrs) {
          draft.attrs.src = recommendation?.logo_url;
        }
      });
    }
  }

  if (mergeTag === 'recommendation.switch' && node.type === 'switch') {
    return (
      <SwitchElement
        id={node.attrs?.id}
        onCheckedChange={() => onChange(recommendation)}
        checked={isChecked || isSelectAllChecked}
        element={node as TSwitchElement}
        isLive={isLive}
        isSelectAllChecked={isSelectAllChecked}
        name={recommendation.isOwner ? 'publication_ids[]' : 'recommendation_ids[]'}
        value={recommendation.isOwner ? recommendation.recommended_publication_id : recommendation.id}
      >
        {null}
      </SwitchElement>
    );
  }

  // Swap out headings && paragraphs
  if ((isParagraph || isHeading) && mergeTag) {
    if (mergeTag === 'recommendation.sponsored' && !recommendation?.sponsored) {
      return null;
    }
    if (mergeTag === 'recommendation.sponsored' && recommendation?.sponsored) {
      node = updateNodeText(node, 'Sponsored') as JSONContent & SerializableNode;
    }

    if (mergeTag === 'recommendation.name') {
      node = updateNodeText(node, recommendation?.name) as JSONContent & SerializableNode;
    }
    if (mergeTag === 'recommendation.description') {
      node = updateNodeText(node, recommendation?.description || '') as JSONContent & SerializableNode;
    }
  }

  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node as TTextLeaf} />;
  }

  const nodeChildren =
    node.content?.map((n, i) =>
      serializeNode(
        {
          node: n as JSONContent & SerializableNode,
          createKey,
          isImageOptimizable,
          recommendation,
          onChange,
          isChecked,
          isLive,
          isSelectAllChecked,
        },
        [...path, i],
      ),
    ) || [];

  return getElement({
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    key,
    children: [...nodeChildren, children],
  });
};

export function RecommendationsSerializer(props: ExtendedSerializerProps) {
  return <>{serializeNode(props)}</>;
}
